import axios, { AxiosRequestConfig } from 'axios';

import { API_ENDPOINT, AUTH_TOKEN_KEY, CLINIKO_SITE_URL_DUBAI, WS_ENDPOINT } from 'src/config/constants';
import { CLINIKO_SITE_URL_LONDON } from 'src/config/constants';
import { clearAllCaches, readFromCache, saveToCache } from 'src/services/api/api.cache';
import { normalizeApiError } from 'src/services/api/api.error';
import type { TApiErrorResponse } from 'src/services/api/api.error';
import isClinicIdDubai from 'src/services/clinic/isClinicIdDubai';

export const getUrl = (endpoint: string): string => {
    if (endpoint.includes('http')) return endpoint;
    return API_ENDPOINT + endpoint;
};

export const getWSUrl = (endpoint?: string): string => {
    endpoint = endpoint || '';
    if (endpoint.startsWith('ws')) return endpoint;
    return WS_ENDPOINT + endpoint;
};

export const getClinikoLondonUrl = (endpoint: string): string => {
    if (endpoint.includes('http')) return endpoint;
    return CLINIKO_SITE_URL_LONDON + endpoint;
};

export const getClinikoDubaiUrl = (endpoint: string): string => {
    if (endpoint.includes('http')) return endpoint;
    return CLINIKO_SITE_URL_DUBAI + endpoint;
};

export const getClinikoUrlByClinic = ({
    endpoint,
    clinicId
}: {
    endpoint: string;
    clinicId: string | number;
}): string => {
    return isClinicIdDubai(clinicId) ? getClinikoDubaiUrl(endpoint) : getClinikoLondonUrl(endpoint);
};

type TRequestOptions = AxiosRequestConfig &
    Partial<{
        credentials: boolean;
        cacheable: boolean;
        alwaysRespond?: boolean;
    }>;

type TApiCallResponse<P> = P | TApiErrorResponse | null;

export const applyOptions = (options: TRequestOptions): Partial<TRequestOptions> => {
    const basicAuth = btoa(`staging:ouronyx`);
    const credentials = localStorage.getItem(AUTH_TOKEN_KEY);
    const { headers, ...params } = options || {};
    const pureHeaders = { ...headers };
    if (options && options.credentials) {
        credentials && (pureHeaders['X-AUTH-TOKEN'] = credentials);
        pureHeaders['Authorization'] = `Basic ${basicAuth}`;
    }
    return {
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...pureHeaders
        },
        ...params
    };
};

// One in-flight request per cacheable endpoint: overlapping callers share its result (data or
// error), and it removes only its own entry when it settles so the next call can retry.
const inFlightGets = new Map<string, Promise<unknown>>();

export async function apiGetCall<P>(
    endpoint: string,
    options?: TRequestOptions
): Promise<TApiCallResponse<P>> {
    if (!options?.cacheable) {
        return fetchGet<P>(endpoint, options);
    }

    const secure = !!options.credentials;
    const cached = readFromCache<P>(endpoint, secure);
    if (cached) {
        return cached;
    }

    // Private requests are scoped to the session that started them.
    const sessionToken = secure ? localStorage.getItem(AUTH_TOKEN_KEY) ?? '' : '';
    const key = secure ? `private:${sessionToken}:${endpoint}` : `public:${endpoint}`;
    const pending = inFlightGets.get(key);
    if (pending) {
        return pending as Promise<TApiCallResponse<P>>;
    }

    const request: Promise<TApiCallResponse<P>> = fetchGet<P>(endpoint, options, sessionToken).finally(() => {
        if (inFlightGets.get(key) === request) {
            inFlightGets.delete(key);
        }
    });
    inFlightGets.set(key, request);

    return request;
}

async function fetchGet<P>(
    endpoint: string,
    options?: TRequestOptions,
    sessionToken?: string
): Promise<TApiCallResponse<P>> {
    try {
        const response = await axios.get(getUrl(endpoint), applyOptions(options || {}));

        // Don't cache a private response if the user changed while it was in flight.
        const sameSession = !options?.credentials || (localStorage.getItem(AUTH_TOKEN_KEY) ?? '') === sessionToken;
        options?.cacheable && sameSession && saveToCache(endpoint, response.data, options?.credentials);

        if (response.headers['ouronyx-clear-api-cache']) {
            clearAllCaches();
        }

        return response.data;
    } catch (err) {
        return normalizeApiError(err, endpoint, 'GET');
    }
}

export async function apiPostCall<P>(
    endpoint: string,
    body?: BodyInit,
    options?: TRequestOptions
): Promise<TApiCallResponse<P | { success: boolean }>> {
    try {
        const response = await axios.post(getUrl(endpoint), body, applyOptions(options || {}));
        if (!response.data && options?.alwaysRespond) {
            return {
                success: response.status >= 200 && response.status < 300
            };
        }

        return response.data;
    } catch (err) {
        return normalizeApiError(err, endpoint, 'POST');
    }
}

export async function apiPutCall<P>(
    endpoint: string,
    body: BodyInit,
    options?: TRequestOptions
): Promise<TApiCallResponse<P>> {
    try {
        const response = await axios.put(getUrl(endpoint), body, applyOptions(options || {}));
        return response.data;
    } catch (err) {
        return normalizeApiError(err, endpoint, 'PUT');
    }
}

export async function apiPatchCall<P>(
    endpoint: string,
    body: BodyInit,
    options?: TRequestOptions
): Promise<TApiCallResponse<P>> {
    try {
        const response = await axios.patch(getUrl(endpoint), body, applyOptions(options || {}));
        return response.data;
    } catch (err) {
        return normalizeApiError(err, endpoint, 'PATCH');
    }
}

export async function apiDeleteCall<P>(
    endpoint: string,
    options?: TRequestOptions
): Promise<TApiCallResponse<P>> {
    try {
        const response = await axios.delete(getUrl(endpoint), applyOptions(options || {}));
        return response.data;
    } catch (err) {
        return normalizeApiError(err, endpoint, 'DELETE');
    }
}
